import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  API_GET_NO_TOKEN,
  API_POST_NO_TOKEN,
  DBRequest,
  useConfig,
} from '../../api/api';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import StarIcon from '@mui/icons-material/Star';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Loader from '../../components/special/Loader';
import Div100vh from 'react-div-100vh';
import Cookies from 'universal-cookie';
import { tokens } from '../../global/theme/tokens';
import * as Yup from 'yup';
import ExoStepper from '../../components/exo/ExoStepper';
import StepCustomStartUp from '../../components/steps/StepCustomStartUp';

const CustomerDataForm = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { type, key } = useParams();
  const config = useConfig();
  const cookies = new Cookies();
  const colorMode = cookies.get('colorMode');
  const { t } = useTranslation();

  const [apiData, setApiData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [steps, setSteps] = useState([]);

  const [isError, setIsError] = useState(false);

  useEffect(() => {
    DBRequest({
      config,
      path: `customer-data-form/${key}`,
      method: API_GET_NO_TOKEN,
      onResponse: handleResponse,
      onLoading: setIsLoading,
      onError: () => setIsError(true),
    });
  }, [config]);

  function handleResponse(res) {
    calcSteps(res.data);
    setApiData(res);
  }
  const fieldsMap = {
    companyLead: [
      {
        label: t('Company Name'),
        key: 'name',
        type: 'text',
      },
      {
        label: t('Website'),
        key: 'url',
        type: 'text',
      },
      {
        label: t('Address'),
        key: 'address',
        type: 'address',
      },
      {
        label: t('E-Mails'),
        type: 'nameValueArray',
        key: 'emails',
        addLabel: t('Add E-Mail'),
        nameKey: 'emailType',
        valueKey: 'emailAddress',
        placeholder: {
          name: t('Label'),
          value: t('E-Mail'),
        },
        radioItems: [
          {
            key: 'isPrimary',
            Icon: StarIcon,
            label: 'Favorite',
          },
          {
            key: 'isInvoiceMail',
            Icon: ReceiptIcon,
            label: 'Invoice E-Mail',
          },
        ],
      },
      {
        label: t('Phonenumbers'),
        type: 'nameValueArray',
        key: 'telephoneNumbers',
        addLabel: t('Add phonenumber'),
        nameKey: 'phoneType',
        valueKey: 'phoneNumber',
        placeholder: {
          name: t('Label'),
          value: t('Phonenumber'),
        },
        radioItems: [
          {
            key: 'isPrimary',
            Icon: StarIcon,
            label: 'Favorite',
          },
        ],
      },
      {
        label: t('Note'),
        key: 'note',
        type: 'richText',
      },
    ],
    personLead: [
      {
        label: t('Salutation'),
        key: 'salutation',
        type: 'text',
      },
      {
        label: t('First Name'),
        key: 'firstName',
        type: 'text',
      },
      {
        label: t('Last Name'),
        key: 'lastName',
        type: 'text',
      },
      {
        label: t('Address'),
        key: 'address',
        type: 'address',
      },
      {
        label: t('E-Mails'),
        type: 'nameValueArray',
        key: 'emails',
        addLabel: t('Add E-Mail'),
        nameKey: 'emailType',
        valueKey: 'emailAddress',
        placeholder: {
          name: t('Label'),
          value: t('E-Mail'),
        },
        radioItems: [
          {
            key: 'isPrimary',
            Icon: StarIcon,
            label: 'Favorite',
          },
          {
            key: 'isInvoiceMail',
            Icon: ReceiptIcon,
            label: 'Invoice E-Mail',
          },
        ],
      },
      {
        label: t('Phonenumbers'),
        type: 'nameValueArray',
        key: 'telephoneNumbers',
        addLabel: t('Add phonenumber'),
        nameKey: 'phoneType',
        valueKey: 'phoneNumber',
        placeholder: {
          name: t('Label'),
          value: t('Phonenumber'),
        },
        radioItems: [
          {
            key: 'isPrimary',
            Icon: StarIcon,
            label: 'Favorite',
          },
        ],
      },
      {
        label: t('Note'),
        key: 'note',
        type: 'richText',
        hidden: type === 'companyLead' ? true : false,
      },
    ],
  };

  const phoneSchema = Yup.object().shape({
    phoneType: Yup.string().required('Is required'),
    phoneNumber: Yup.string().required('Is required'),
  });
  const emailSchema = Yup.object().shape({
    emailType: Yup.string().required('Is required'),
    emailAddress: Yup.string().required('Is required'),
  });

  const validationSchemaCompanyLead = Yup.object().shape({
    name: Yup.string().required(t('Is required')),
    url: Yup.string().required(t('Is required')),
    telephoneNumbers: Yup.array().of(phoneSchema).min(1).required(),
    emails: Yup.array().of(emailSchema).min(1).required(),
    address: Yup.object().shape({
      street: Yup.string().required(t('Is required')),
      houseNumber: Yup.string().required(t('Is required')),
      zipCode: Yup.string().required(t('Is required')),
      city: Yup.string().required(t('Is required')),
    }),
  });

  const validationSchemaPersonLead = Yup.object().shape({
    salutation: Yup.string().required(t('Is required')),
    firstName: Yup.string().required(t('Is required')),
    lastName: Yup.string().required(t('Is required')),
    telephoneNumbers: Yup.array().of(phoneSchema).min(1).required(),
    emails: Yup.array().of(emailSchema).min(1).required(),
    address:
      type === 'companyLead' && !apiData?.data?.personLeadExists
        ? Yup.object().shape({
            street: Yup.string().notRequired(),
            houseNumber: Yup.string().notRequired(),
            zipCode: Yup.string().notRequired(),
            city: Yup.string().notRequired(),
          })
        : Yup.object().shape({
            street: Yup.string().required(t('Is required')),
            houseNumber: Yup.string().required(t('Is required')),
            zipCode: Yup.string().required(t('Is required')),
            city: Yup.string().required(t('Is required')),
          }),
  });

  const schemaMap = {
    companyLead: validationSchemaCompanyLead,
    personLead: validationSchemaPersonLead,
  };

  const dynamicItemSchema = Yup.lazy((item) => {
    if (!item) return Yup.object({});

    switch (item.type) {
      case 'text':
        return Yup.object({
          minValue: Yup.string().required('Number is required'),
        });
      case 'number':
        return Yup.object({
          minValue: Yup.number()
            .typeError('Must be a number')
            .required('Min value is required'),
          maxValue: Yup.number()
            .typeError('Must be a number')
            .required('Max value is required'),
        });
      case 'checkbox':
        return Yup.mixed();
      case 'select':
        return Yup.object({
          minValue: Yup.array().min(1).required('Number is required'),
        });
      default:
        return Yup.object({
          minValue: Yup.mixed().required(),
        });
    }
  });

  const propSchema = Yup.object({
    // The key must match what your validateDataCollection expects
    properties: Yup.array().of(dynamicItemSchema),
  });

  function calcSteps(data) {
    var newSteps = [
      {
        label: t('Core Data'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepCustomStartUp
            fields={fieldsMap[type]}
            updateDataCollection={(insertData) =>
              updateDataCollection((prev) => ({
                ...prev,
                [type]: { ...prev[type], ...insertData },
              }))
            }
            dataCollection={dataCollection[type]}
            updateValidation={updateValidation}
            validationSchema={schemaMap[type]}
          />
        ),
      },
    ];
    // person lead data does'nt exists
    if (!data?.personLeadExists && type === 'companyLead') {
      newSteps.push({
        label: t('Personal Data'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepCustomStartUp
            fields={fieldsMap['personLead']}
            updateDataCollection={(insertData) =>
              updateDataCollection((prev) => ({
                ...prev,
                ['personLead']: { ...prev['personLead'], ...insertData },
              }))
            }
            dataCollection={dataCollection['personLead']}
            updateValidation={updateValidation}
            validationSchema={schemaMap['personLead']}
          />
        ),
      });
    }
    // has properties
    if (
      (data.companyLead?.properties &&
        data.companyLead?.properties.length > 0) ||
      (data.personLead?.properties && data.personLead?.properties.length > 0)
    ) {
      const fields = [
        {
          label: t('Purchasing profile'),
          key: 'properties',
          type: 'salesObjectPropertiesCustomer',
          required: true,
        },
      ];

      newSteps.push({
        label: t('Interests'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepCustomStartUp
            fields={fields}
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
            validationSchema={propSchema}
          />
        ),
      });
    }
    setSteps(newSteps);
  }

  const finalStep = {
    operation: ({
      dataCollection,
      handleResponse,
      setIsLoading,
      handleError,
    }) => {
      setIsLoading(true);

      if (dataCollection.properties) {
        if (dataCollection.companyLead) {
          dataCollection = {
            ...dataCollection,
            companyLead: {
              ...dataCollection.companyLead,
              properties: dataCollection.properties,
            },
          };
        } else if (dataCollection.personLead) {
          dataCollection = {
            ...dataCollection,
            personLead: {
              ...dataCollection.personLead,
              properties: dataCollection.properties,
            },
          };
        }
      }

      DBRequest({
        config,
        path: `customer-data-form/${key}`,
        method: API_POST_NO_TOKEN(dataCollection),
        onResponse: handleResponse,
        onLoading: setIsLoading,
        onError: handleError,
      });
    }, // your operation with the data
    content: (response) => (
      <Box>
        <Typography>{t('Thank you for filling out the form')}</Typography>
      </Box>
    ), // component with final operations and the feedback
    noClose: true,
  };

  if (isError)
    return (
      <Box className="w-full h-full flex justify-center items-center">
        <Typography fontSize={20}>
          {t('Your token is expired or invalid')}
        </Typography>
      </Box>
    );

  return (
    <Div100vh>
      <img
        src={
          colorMode === 'dark'
            ? '/assets/uploads/background-dark.webp'
            : '/assets/uploads/background-bright.webp'
        }
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          position: 'absolute',
          top: '0',
          left: '0',
          objectPosition: '32%',
          zIndex: '-5',
        }}
      />
      <Box className="h-full flex flex-col backdrop-blur-lg">
        <img
          src={
            colorMode === 'dark'
              ? '/assets/svg/exopaper-text-darkmode.svg'
              : '/assets/svg/exopaper-text.svg'
          }
          className="h-10 lg:absolute top-0 right-5 mr-auto ml-4 mt-5"
        />
        <Loader active={isLoading} />
        {apiData && (
          <Box
            className=" max-w-[600px] w-full mx-auto px-2 lg:px-4 py-2 lg:py-3 rounded-lg mt-4 lg:mt-10"
            sx={{ bgcolor: colors.card }}
          >
            <ExoStepper
              startDataCollection={{
                ...apiData?.data,
                properties:
                  apiData?.data.companyLead?.properties ??
                  apiData?.data.personLead?.properties,
              }}
              label={t('Complete your data')}
              data={steps}
              finalStep={finalStep}
            />
          </Box>
        )}
      </Box>
    </Div100vh>
  );
};

export default CustomerDataForm;
